import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import { Search, CheckCircle, Cancel } from '@mui/icons-material';
import { buildMatchingGroupsMapFromTables, forEachMatchingInfo } from './matchingUtils';

const CARD_STYLE = {
  borderRadius: 2,
  border: '1px solid #e0e0e0',
  boxShadow: 'none',
};

const HEADER_CELL_STYLE = {
  backgroundColor: '#f5f5f5',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: 13,
};

/**
 * Build a cross-country SKU → groupName map from all matching groups.
 * Intentionally ignores country/retailer constraints so that the same group name
 * across multiple countries resolves to one row.
 */
function buildSkuToGroupMap(matchingGroups) {
  // matchingGroups: Map<sku, membership | membership[]> — first membership wins per SKU for display row
  const skuToGroup = new Map();
  forEachMatchingInfo(matchingGroups, (info, sku) => {
    if (!skuToGroup.has(sku)) {
      skuToGroup.set(sku, info.groupName);
    }
  });
  return skuToGroup;
}

function ProductAvailabilityView() {
  const { projectKey } = useParams();

  const [rawData, setRawData]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [matchingGroups, setMatchingGroups] = useState(new Map());

  // Filters
  const [selectedBrand,    setSelectedBrand]    = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [searchTerm,       setSearchTerm]       = useState('');

  // Unique filter values
  const [brands,    setBrands]    = useState([]);
  const [categories, setCategories] = useState([]);
  const [retailers, setRetailers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dataResponse, overridesResponse, matchingResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
        ]);
        const { columns, data } = dataResponse.data;
        const overrides = overridesResponse?.data?.overrides || {};

        const processed = data.map((row) => {
          const obj = {};
          columns.forEach((col, i) => { obj[col] = row[i]; });
          const override = obj.title ? overrides[obj.title] : null;
          if (override) obj.category_group = override;
          return obj;
        });

        setRawData(processed);
        setMatchingGroups(buildMatchingGroupsMapFromTables(matchingResponse?.data?.tables || {}));
        setBrands([...new Set(processed.map((d) => d.brand).filter(Boolean))].sort());
        setCategories([...new Set(processed.map((d) => d.category_group).filter(Boolean))].sort());
        setRetailers([...new Set(processed.map((d) => d.retailer || d.shop || d.source).filter(Boolean))].sort());
      } catch (err) {
        console.error('Error fetching availability data:', err);
        setError('Failed to load product availability data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- Availability matrix ---
  // Rows  = product / group name  (cross-country: same group name → one row)
  // Columns = countries
  // Cell  = ✓ if any SKU of the group appears in that country
  const { displayCountries, rows, summaryStats } = useMemo(() => {
    let filtered = rawData;
    if (selectedBrand)    filtered = filtered.filter((d) => d.brand === selectedBrand);
    if (selectedCategory) filtered = filtered.filter((d) => d.category_group === selectedCategory);
    if (selectedRetailer) filtered = filtered.filter((d) => (d.retailer || d.shop || d.source) === selectedRetailer);

    // Cross-country group resolution: look up group name by SKU title only (no country/retailer gate)
    const skuToGroup = buildSkuToGroupMap(matchingGroups);
    const resolveTitle = (sku) => {
      const groupName = skuToGroup.get(sku);
      return groupName ? { title: groupName, isGroup: true } : { title: sku, isGroup: false };
    };

    // Build presence map: displayTitle → { country: true }
    const presenceMap = {};
    const metaMap     = {}; // displayTitle → { brand, category, isGroup }

    filtered.forEach((d) => {
      const country = d.country;
      if (!d.title || !country) return;
      const { title, isGroup } = resolveTitle(d.title);
      if (!presenceMap[title]) presenceMap[title] = {};
      presenceMap[title][country] = true;
      if (!metaMap[title]) {
        metaMap[title] = { brand: d.brand || '', category: d.category_group || '', isGroup };
      }
    });

    // Search filter (after group resolution so users can search by group name)
    const allTitles = Object.keys(presenceMap).filter((t) =>
      !searchTerm || t.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const uniqueCountries = [...new Set(filtered.map((d) => d.country).filter(Boolean))].sort();

    const tableRows = allTitles.map((title) => ({
      title,
      brand:    metaMap[title]?.brand    || '',
      category: metaMap[title]?.category || '',
      presence: presenceMap[title] || {},
      count:    Object.keys(presenceMap[title] || {}).length,
      isGroup:  metaMap[title]?.isGroup  || false,
    }));

    tableRows.sort((a, b) => b.count - a.count);

    const totalProducts  = allTitles.length;
    const totalCountries = uniqueCountries.length;
    const avgCoverage    = totalProducts > 0
      ? Math.round(tableRows.reduce((s, r) => s + r.count, 0) / totalProducts * 10) / 10
      : 0;
    const fullCoverage = tableRows.filter((r) => r.count === totalCountries).length;

    return {
      displayCountries: uniqueCountries,
      rows: tableRows,
      summaryStats: { totalProducts, totalCountries, avgCoverage, fullCoverage },
    };
  }, [rawData, matchingGroups, selectedBrand, selectedCategory, selectedRetailer, searchTerm]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Product Availability
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Which products / groups are listed across countries.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ ...CARD_STYLE, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" placeholder="Search product / group…"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select value={selectedBrand} label="Brand" onChange={(e) => setSelectedBrand(e.target.value)}>
                  <MenuItem value="">All brands</MenuItem>
                  {brands.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={selectedCategory} label="Category" onChange={(e) => setSelectedCategory(e.target.value)}>
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Retailer</InputLabel>
                <Select value={selectedRetailer} label="Retailer" onChange={(e) => setSelectedRetailer(e.target.value)}>
                  <MenuItem value="">All retailers</MenuItem>
                  {retailers.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Availability matrix — rows = products/groups, columns = countries */}
      <Card sx={CARD_STYLE}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No products match the current filters.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEADER_CELL_STYLE, minWidth: 240, position: 'sticky', left: 0, zIndex: 3 }}>
                      Product / Group
                    </TableCell>
                    <TableCell sx={{ ...HEADER_CELL_STYLE, minWidth: 100 }}>Brand</TableCell>
                    <TableCell sx={{ ...HEADER_CELL_STYLE, minWidth: 110 }}>Category</TableCell>
                    <TableCell sx={{ ...HEADER_CELL_STYLE, minWidth: 70 }} align="center"># Countries</TableCell>
                    <TableCell sx={{ ...HEADER_CELL_STYLE, minWidth: 90 }} align="center">Coverage</TableCell>
                    {displayCountries.map((c) => (
                      <TableCell key={c} sx={{ ...HEADER_CELL_STYLE, minWidth: 80 }} align="center">
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const coverage = summaryStats.totalCountries > 0
                      ? Math.round((row.count / summaryStats.totalCountries) * 100)
                      : 0;
                    return (
                      <TableRow key={row.title} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 500, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                          <Tooltip title={row.title}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 260 }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                {row.title}
                              </span>
                              {row.isGroup && (
                                <Chip
                                  label="group"
                                  size="small"
                                  sx={{ fontSize: 10, height: 16, backgroundColor: '#e3f2fd', color: '#1565c0', flexShrink: 0 }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{row.brand || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {row.category
                            ? <Chip label={row.category} size="small" sx={{ fontSize: 11 }} />
                            : '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 13, fontWeight: 700 }}>
                          {row.count}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 50, height: 6, borderRadius: 3, backgroundColor: '#e0e0e0', overflow: 'hidden' }}>
                              <Box sx={{
                                height: '100%', width: `${coverage}%`, borderRadius: 3,
                                backgroundColor: coverage === 100 ? '#388e3c' : coverage >= 50 ? '#f57c00' : '#d32f2f',
                              }} />
                            </Box>
                            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
                              {coverage}%
                            </Typography>
                          </Box>
                        </TableCell>
                        {displayCountries.map((c) => (
                          <TableCell key={c} align="center">
                            {row.presence[c]
                              ? <CheckCircle sx={{ fontSize: 16, color: '#388e3c' }} />
                              : <Cancel     sx={{ fontSize: 16, color: '#e0e0e0' }} />}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ProductAvailabilityView;
